import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { NotificationsActive, History, CheckCircle } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { reuniaoService } from '@/services/reuniaoService'
import { AgendamentoPendente, LembreteAgendamento } from '@/types/reuniao'

/**
 * Etapa 2 do PDF "Jornada de Análise das Reservas pela Originei" — Acompanhamento do
 * Agendamento.
 *
 * O objetivo é garantir a realização da primeira reunião entre Consultor, Cliente e
 * Originei. A tela é a agenda vista pelo avesso: em vez das reuniões que existem, mostra
 * as que **deveriam existir e não foram marcadas**, para o Operador cobrar o Consultor.
 */

const formatarData = (iso?: string): string =>
  iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : '—'

/**
 * A espera é o que dá urgência à linha. Os cortes são de leitura, não regra de negócio:
 * servem para o olho achar o caso antigo numa lista longa.
 */
const corDaEspera = (dias: number): 'default' | 'warning' | 'error' =>
  dias >= 14 ? 'error' : dias >= 7 ? 'warning' : 'default'

const AgendamentosPendentesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()

  const [pendentes, setPendentes] = useState<AgendamentoPendente[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [alvo, setAlvo] = useState<AgendamentoPendente | null>(null)
  const [mensagem, setMensagem] = useState('')
  const [enviando, setEnviando] = useState(false)

  const [historicoDe, setHistoricoDe] = useState<AgendamentoPendente | null>(null)
  const [historico, setHistorico] = useState<LembreteAgendamento[] | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setPendentes(await reuniaoService.agendamentosPendentes())
    } catch {
      setErro('Não foi possível carregar os agendamentos pendentes.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const enviarLembrete = async () => {
    if (!alvo) return
    setEnviando(true)
    try {
      await reuniaoService.enviarLembrete(alvo.oportunidadeId, mensagem.trim() || undefined)
      enqueueSnackbar('Lembrete enviado ao consultor.', { variant: 'success' })
      setAlvo(null)
      setMensagem('')
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao enviar o lembrete.', { variant: 'error' })
    } finally {
      setEnviando(false)
    }
  }

  const abrirHistorico = async (linha: AgendamentoPendente) => {
    setHistoricoDe(linha)
    setHistorico(null)
    try {
      setHistorico(await reuniaoService.historicoLembretes(linha.oportunidadeId))
    } catch {
      setHistorico([])
      enqueueSnackbar('Falha ao carregar o histórico.', { variant: 'error' })
    }
  }

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando agendamentos pendentes" />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Agendamentos pendentes
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Reservas em análise cuja primeira reunião com o cliente ainda não foi marcada.
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button size="small" color="inherit" onClick={() => void carregar()}>
            Tentar de novo
          </Button>
        }>
          {erro}
        </Alert>
      )}

      {!erro && pendentes.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Nenhum agendamento pendente
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Todas as reservas em análise já têm a primeira reunião marcada.
          </Typography>
        </Card>
      ) : (
        <Card>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reserva</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell align="right">Aguardando</TableCell>
                  <TableCell align="right">Cobranças</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendentes.map((linha) => (
                  <TableRow key={linha.oportunidadeId} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      RES-{linha.numero ?? '—'}
                    </TableCell>
                    <TableCell>
                      {linha.razaoSocial ?? '—'}
                      {linha.cnpj && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {linha.cnpj}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={linha.status} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${linha.diasDesdeEnvio} dia(s)`}
                        size="small"
                        color={corDaEspera(linha.diasDesdeEnvio)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {linha.lembretesEnviados === 0 ? (
                        <Typography variant="caption" color="textSecondary">
                          nunca cobrado
                        </Typography>
                      ) : (
                        <Tooltip title={`Última em ${formatarData(linha.ultimoLembreteEm)}`}>
                          <span>{linha.lembretesEnviados}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<NotificationsActive />}
                          onClick={() => setAlvo(linha)}
                        >
                          Cobrar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<History />}
                          onClick={() => void abrirHistorico(linha)}
                          disabled={linha.lembretesEnviados === 0}
                        >
                          Histórico
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={alvo !== null} onClose={() => setAlvo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cobrar agendamento — RES-{alvo?.numero ?? ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            O consultor responsável recebe uma notificação pedindo que marque a primeira
            reunião com {alvo?.razaoSocial ?? 'o cliente'}.
          </Typography>
          <TextField
            label="Recado (opcional)"
            fullWidth
            multiline
            minRows={3}
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            placeholder="Ex.: o cliente já pediu retorno duas vezes."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlvo(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void enviarLembrete()} disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar lembrete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historicoDe !== null}
        onClose={() => setHistoricoDe(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Histórico do agendamento — RES-{historicoDe?.numero ?? ''}</DialogTitle>
        <DialogContent>
          {historico === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : historico.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhuma cobrança registrada.
            </Typography>
          ) : (
            <Stack divider={<Divider />} spacing={1.5}>
              {historico.map((l) => (
                <Box key={l.id}>
                  <Typography variant="caption" color="textSecondary">
                    {formatarData(l.enviadoEm)} · {l.enviadoPorNome ?? 'Originei'}
                  </Typography>
                  <Typography variant="body2">
                    {l.mensagem ?? 'Lembrete enviado sem recado.'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoDe(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AgendamentosPendentesPage
